import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Database, Globe2, FileText, Users, Lock, AlertTriangle, ArrowRight } from 'lucide-react';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import { Aurora, Reveal, SectionDivider } from '../components/ui/depth';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';

interface SubProcessor {
  name: string;
  purpose: string;
  region: string;
  dataTypes: string;
}

const subProcessors: SubProcessor[] = [
  { name: 'Google Firebase (Auth, Firestore, Cloud Storage, Cloud Functions)', purpose: 'Authentication, application database, file storage, server-side compute', region: 'us-central1 (Iowa, USA) — multi-region replication', dataTypes: 'Account details, uploaded documents, learning profile, activity' },
  { name: 'Anthropic (Claude API)', purpose: 'AI tutor responses, diagnostic reasoning, content generation', region: 'United States', dataTypes: 'Uploaded content excerpts, CogniPrint profile, tutor conversation' },
  { name: 'OpenAI', purpose: 'Image generation for visual learning content', region: 'United States', dataTypes: 'Concept descriptions (no personal data)' },
  { name: 'Lemon Squeezy', purpose: 'Payment processing, subscription management, invoicing', region: 'United States', dataTypes: 'Billing details, subscription status, payment method (tokenised)' },
  { name: 'YouTube (Data API)', purpose: 'Video recommendations within the Tutor', region: 'Global', dataTypes: 'Concept queries (no personal data)' },
];

interface CommitmentRow {
  icon: typeof Shield;
  title: string;
  body: string;
}

const commitments: CommitmentRow[] = [
  {
    icon: Database,
    title: 'Your content stays yours',
    body: 'We do not use your uploaded content to train AI models. Uploaded documents are processed to generate your learning experience and deleted from Cloud Storage after TTL expiry. The derived structured data (concepts, learning paths) is retained only while your account is active.',
  },
  {
    icon: Globe2,
    title: 'Data residency and transfers',
    body: 'CogniPrint infrastructure runs primarily on Google Cloud (us-central1) with multi-region replication for availability. For data transfers from South Africa / the EEA, we rely on Google\'s and Anthropic\'s Standard Contractual Clauses. Users in the EU may exercise GDPR rights; users in South Africa may exercise POPIA rights.',
  },
  {
    icon: Lock,
    title: 'Encryption in transit and at rest',
    body: 'All traffic is served over TLS 1.2+. Firestore and Cloud Storage encrypt data at rest with Google-managed keys. Secrets and API keys are stored in Google Secret Manager.',
  },
  {
    icon: Users,
    title: 'Access controls',
    body: 'User-level data is accessed only by the user\'s authenticated session or by CogniPrint staff for documented support requests. Admin access is gated behind role-based Firestore rules. Cloud Function invocations require a valid authenticated caller.',
  },
  {
    icon: AlertTriangle,
    title: 'Incident response',
    body: 'We commit to notifying affected users within 72 hours of confirming a personal-data breach. Our incident response runbook is available on request to enterprise customers under NDA.',
  },
  {
    icon: FileText,
    title: 'DPA available on request',
    body: 'A Data Processing Agreement covering POPIA, GDPR, and UK-GDPR obligations is available for business customers. Contact legal@cogniprint.io with "DPA request" in the subject.',
  },
];

export function TrustPage() {
  return (
    <div className="min-h-screen bg-dark-bg relative overflow-hidden">
      <RidgeTexture opacity="opacity-[0.04]" />
      <Aurora palette="indigo" intensity={0.4} />

      {/* Hero */}
      <section className="relative pt-40 pb-12 px-4 text-center">
        <div className="max-w-3xl mx-auto relative">
          <Reveal>
            <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-pill text-xs font-semibold bg-indigo-500/12 text-indigo-300 border border-indigo-500/30">
              <Shield size={12} /> Trust &amp; compliance
            </span>
          </Reveal>
          <Reveal>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-5 leading-[1.1]">
              How we handle{' '}
              <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">your data</span>
            </h1>
          </Reveal>
          <Reveal>
            <p className="text-content-secondary font-body text-lg leading-relaxed max-w-2xl mx-auto">
              CogniPrint is built around a single product idea — your learning fingerprint is yours.
              That commitment only matters if the operational posture backs it up. Here's what ours looks like today.
            </p>
          </Reveal>
          <Reveal>
            <p className="text-xs text-content-dim font-body mt-4">Last updated: April 2026 · CogniPrint (Pty) Ltd · A Kilonova Ventures subsidiary</p>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      {/* Commitments */}
      <section className="relative py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-2">Our commitments</h2>
            <p className="text-content-muted font-body text-sm">Operational posture, not marketing copy.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-4">
            {commitments.map((row) => (
              <Reveal key={row.title}>
                <div className="h-full rounded-xl border border-dark-border bg-dark-surface/60 p-5">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-3">
                    <row.icon size={18} className="text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-1.5">{row.title}</h3>
                  <p className="text-sm text-content-secondary font-body leading-relaxed">{row.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Sub-processor list */}
      <section className="relative py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-8">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-2">Sub-processors</h2>
            <p className="text-content-muted font-body text-sm max-w-2xl mx-auto">
              Third-party services we use to deliver CogniPrint. We review this list when onboarding new vendors
              and notify customers of material changes via email.
            </p>
          </Reveal>

          <Reveal>
            <div className="rounded-2xl border border-dark-border bg-dark-surface/60 overflow-hidden">
              <div className="hidden md:grid grid-cols-12 gap-2 px-5 py-3 text-[11px] font-body font-bold uppercase tracking-wider text-content-muted bg-dark-bg/40 border-b border-dark-border">
                <div className="col-span-4">Service</div>
                <div className="col-span-3">Purpose</div>
                <div className="col-span-3">Region</div>
                <div className="col-span-2">Data types</div>
              </div>
              {subProcessors.map((sp) => (
                <div
                  key={sp.name}
                  className="md:grid md:grid-cols-12 md:gap-2 px-5 py-4 text-sm font-body border-t border-dark-border first:border-t-0 md:first:border-t-0"
                >
                  <div className="md:col-span-4 font-semibold text-content mb-1 md:mb-0">{sp.name}</div>
                  <div className="md:col-span-3 text-content-secondary mb-1 md:mb-0">
                    <span className="md:hidden text-[10px] uppercase tracking-wider text-content-dim mr-2">Purpose:</span>
                    {sp.purpose}
                  </div>
                  <div className="md:col-span-3 text-content-secondary mb-1 md:mb-0">
                    <span className="md:hidden text-[10px] uppercase tracking-wider text-content-dim mr-2">Region:</span>
                    {sp.region}
                  </div>
                  <div className="md:col-span-2 text-content-muted text-xs">
                    <span className="md:hidden text-[10px] uppercase tracking-wider text-content-dim mr-2">Data:</span>
                    {sp.dataTypes}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      {/* Your rights */}
      <section className="relative py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-8">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-2">Your rights (POPIA &amp; GDPR)</h2>
          </Reveal>

          <Reveal>
            <div className="rounded-xl border border-dark-border bg-dark-surface/60 p-6 space-y-4 text-sm font-body text-content-secondary leading-relaxed">
              <p>
                <strong className="text-content">Access.</strong> Request a copy of the personal data we hold about you.
              </p>
              <p>
                <strong className="text-content">Correction.</strong> Ask us to correct inaccurate information in your profile
                or account. You can update most of this yourself from the Profile page.
              </p>
              <p>
                <strong className="text-content">Deletion.</strong> Delete your account and associated data at any time
                from{' '}
                <Link to="/profile" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                  your Profile page
                </Link>
                . Deletion is permanent and removes your uploads, learning paths, tutor sessions, and recalibration history.
              </p>
              <p>
                <strong className="text-content">Portability.</strong> Export your data in a machine-readable format.
                Contact support and we'll send a structured export within 30 days.
              </p>
              <p>
                <strong className="text-content">Objection.</strong> Object to specific processing activities —
                for example, declining tutor analytics. Contact support to discuss specifics.
              </p>
              <p>
                <strong className="text-content">Withdrawal of consent.</strong> Where we rely on consent (e.g. optional
                emails), you can withdraw that consent without affecting lawfulness of processing before withdrawal.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-6 text-center">
            <p className="text-sm text-content-muted font-body">
              To exercise any of these rights, email{' '}
              <a href="mailto:legal@cogniprint.io" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                legal@cogniprint.io
              </a>
              . We respond within 30 days (POPIA) / within one month (GDPR).
            </p>
          </Reveal>
        </div>
      </section>

      <SectionDivider />

      {/* Contacts / CTA */}
      <section className="relative py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="rounded-2xl border border-dark-border bg-dark-surface/60 p-6 sm:p-8">
              <h2 className="font-heading font-bold text-xl sm:text-2xl mb-4">Contacts</h2>
              <dl className="grid sm:grid-cols-2 gap-5 text-sm font-body">
                <div>
                  <dt className="text-content-muted mb-0.5">Privacy enquiries</dt>
                  <dd><a href="mailto:legal@cogniprint.io" className="text-indigo-400 hover:text-indigo-300 transition-colors">legal@cogniprint.io</a></dd>
                </div>
                <div>
                  <dt className="text-content-muted mb-0.5">Security disclosure</dt>
                  <dd><a href="mailto:legal@cogniprint.io" className="text-indigo-400 hover:text-indigo-300 transition-colors">legal@cogniprint.io</a></dd>
                </div>
                <div>
                  <dt className="text-content-muted mb-0.5">DPA / procurement</dt>
                  <dd><a href="mailto:legal@cogniprint.io?subject=DPA request" className="text-indigo-400 hover:text-indigo-300 transition-colors">legal@cogniprint.io</a></dd>
                </div>
                <div>
                  <dt className="text-content-muted mb-0.5">Information Officer (POPIA)</dt>
                  <dd className="text-content">CogniPrint (Pty) Ltd — appointed officer on request</dd>
                </div>
              </dl>
            </div>
          </Reveal>

          <Reveal className="mt-8 text-center">
            <Link to="/privacy" className="inline-flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 font-body transition-colors">
              Read the full Privacy Policy <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Business footer */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative py-16 px-4 text-center"
      >
        <div className="max-w-2xl mx-auto">
          <h3 className="font-heading font-bold text-xl sm:text-2xl mb-3">Evaluating CogniPrint for your organisation?</h3>
          <p className="text-content-secondary font-body text-sm mb-6">
            We're happy to walk through our security posture, complete vendor questionnaires, and sign a DPA before you commit to a pilot.
          </p>
          <Link to="/for-teams">
            <Button variant="gold">
              Talk to us about Teams <ArrowRight size={14} />
            </Button>
          </Link>
        </div>
      </motion.section>

      <Footer />
    </div>
  );
}
